import { Box, ButtonBase, Slide, Stack } from '@mui/material';
import {
  ArrowClockwiseIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  ClockIcon,
  PulseIcon,
  WarningOctagonIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { FlowBlockState } from 'flow-schema';
import { type ReactNode, useRef } from 'react';

import { colors, isDefined } from 'common';

import { useReactFlowViewContext } from '../../../context/context';
import { FlowRunBlockDataSource } from '../../../types';

interface DecorationStyle {
  icon: ReactNode;
  content: string;
  color: string;
  backgroundColor: string;
  borderColor: string;
}

const BlockRunStatusDecorations: Partial<Record<FlowBlockState, DecorationStyle>> = {
  [FlowBlockState.Queued]: {
    icon: <CircleNotchIcon size={12} />,
    color: colors.extended.gray[25],
    backgroundColor: colors.extended.gray[93],
    borderColor: colors.extended.gray[65],
    content: 'Queued',
  },
  [FlowBlockState.Waiting]: {
    icon: <ClockIcon size={12} />,
    color: colors.extended.gray[25],
    backgroundColor: colors.extended.gray[93],
    borderColor: colors.extended.gray[65],
    content: 'Waiting',
  },
  [FlowBlockState.Running]: {
    icon: <PulseIcon size={12} />,
    color: 'success.dark',
    backgroundColor: colors.success.light,
    borderColor: 'success.main',
    content: 'Running',
  },
  [FlowBlockState.PendingRetry]: {
    icon: <PulseIcon size={12} />,
    color: colors.warning.dark,
    backgroundColor: colors.warning.light,
    borderColor: 'warning.main',
    content: 'Pending Retry',
  },
  [FlowBlockState.Completed]: {
    icon: <CheckCircleIcon size={12} />,
    color: 'success.dark',
    backgroundColor: colors.success.light,
    borderColor: 'success.main',
    content: 'Completed',
  },
  [FlowBlockState.Invalidated]: {
    icon: <XCircleIcon size={12} />,
    color: colors.extended.gray[25],
    backgroundColor: colors.extended.gray[93],
    borderColor: colors.extended.gray[65],
    content: 'Skipped',
  },
  [FlowBlockState.Failed]: {
    icon: <WarningOctagonIcon size={12} />,
    color: 'error.dark',
    backgroundColor: colors.error.light,
    borderColor: 'error.main',
    content: 'Error',
  },
};

const InheritedBlockDecorations: DecorationStyle = {
  icon: <ArrowClockwiseIcon size={12} />,
  color: 'success.dark',
  backgroundColor: colors.success.light,
  borderColor: 'success.main',
  content: 'Last run data',
};

const OFFSET_IN_PIXELS = 28;

interface BlockExecutionStatusOverlayProps {
  nodeId: string;
  blockExecutionState: FlowBlockState | undefined;
  dataSourceType?: FlowRunBlockDataSource | undefined;
}

export function BlockExecutionStatusOverlay(props: BlockExecutionStatusOverlayProps) {
  const { nodeId, blockExecutionState, dataSourceType } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const { setSelectedNodeId } = useReactFlowViewContext();
  if (!blockExecutionState) {
    return null;
  }
  let decorations;
  if (dataSourceType === FlowRunBlockDataSource.Inherited) {
    decorations = InheritedBlockDecorations;
  } else {
    decorations = BlockRunStatusDecorations[blockExecutionState];
  }

  const { icon: icon, backgroundColor, color, borderColor, content } = decorations ?? {};
  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        top: -OFFSET_IN_PIXELS,
        right: 0,
        height: OFFSET_IN_PIXELS,
        overflow: 'hidden',
      }}
    >
      <Slide
        in={isDefined(decorations)}
        direction="up"
        // eslint-disable-next-line react-hooks/refs
        container={containerRef.current}
      >
        <ButtonBase
          aria-label="Select block"
          onClick={() => {
            setSelectedNodeId(nodeId);
          }}
        >
          <Stack
            component="span"
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{
              backgroundColor,
              borderRadius: 2,
              paddingX: 1,
              paddingY: 0.25,
              border: '1px solid',
              color,
              borderColor,
              fontSize: '0.75rem',
              minWidth: 64,
              minHeight: '1rem',
              opacity:
                blockExecutionState === FlowBlockState.Invalidated
                  ? 0.6
                  : blockExecutionState === FlowBlockState.Pending
                    ? 0.7
                    : undefined,
            }}
          >
            {icon}
            <span>
              <b>{content}</b>
            </span>
          </Stack>
        </ButtonBase>
      </Slide>
    </Box>
  );
}
